
// ABOUTME: Page for viewing referrals filtered by a specific job role
// ABOUTME: Displays detailed referral information and provides navigation back to role management

import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Users, TrendingUp, CheckCircle, Clock } from 'lucide-react';
import { useReferralsByRole } from '@/hooks/useReferralsByRole';
import { useJobRoles } from '@/hooks/useJobRoles';
import ReferralList from '@/components/referrals/ReferralList';

const ReferralViewPage = () => {
  const { roleId } = useParams<{ roleId: string }>();
  const navigate = useNavigate();
  const { referrals, isLoading: referralsLoading } = useReferralsByRole(roleId || '');
  const { jobRoles, isLoading: rolesLoading } = useJobRoles();

  const currentRole = jobRoles?.find(role => role.id === roleId);

  if (referralsLoading || rolesLoading) {
    return (
      <div className="p-6">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Loading referrals...</p>
        </div>
      </div>
    );
  }

  if (!currentRole) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="p-8 text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Role Not Found</h2>
            <p className="text-gray-600 mb-4">The requested role could not be found.</p>
            <Button onClick={() => navigate('/manage-roles')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Manage Roles
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const activeReferrals = referrals?.filter(ref => 
    ['submitted', 'under_review', 'interview_scheduled', 'interviewed'].includes(ref.status || '')
  ).length || 0;

  const hiredCount = referrals?.filter(ref => ref.status === 'hired').length || 0;
  const totalReferrals = referrals?.length || 0;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <div className="flex items-center space-x-3">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => navigate('/manage-roles')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{currentRole.title}</h1>
              <p className="text-gray-600">
                {currentRole.department} • {currentRole.location}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Referrals</p>
                <p className="text-xl font-bold">{totalReferrals}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <div className="p-2 bg-gray-100 rounded-lg">
                <Clock className="w-4 h-4 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Active</p>
                <p className="text-xl font-bold">{activeReferrals}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-4 h-4 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Hired</p>
                <p className="text-xl font-bold">{hiredCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <div className="p-2 bg-purple-100 rounded-lg">
                <TrendingUp className="w-4 h-4 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Bonus Per Hire</p>
                <p className="text-xl font-bold">${currentRole.bonus_amount.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Role Details */}
      <Card>
        <CardHeader>
          <CardTitle>Role Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Department</p>
              <p className="font-medium">{currentRole.department}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Location</p>
              <p className="font-medium">{currentRole.location}</p>
            </div>
            {currentRole.salary_range && (
              <div>
                <p className="text-sm text-gray-600">Salary Range</p>
                <p className="font-medium">{currentRole.salary_range}</p>
              </div>
            )}
            <div>
              <p className="text-sm text-gray-600">Status</p>
              <p className="font-medium">{currentRole.is_active ? 'Active' : 'Inactive'}</p>
            </div>
          </div>
          {currentRole.description && (
            <div className="mt-4">
              <p className="text-sm text-gray-600">Description</p>
              <p className="mt-1">{currentRole.description}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Referrals List */}
      <ReferralList referrals={referrals || []} roleTitle={currentRole.title} />
    </div>
  );
};

export default ReferralViewPage;
